import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { FileSignature, ArrowRight, Share2, ShieldCheck, PenLine } from "lucide-react";

function statusTone(s) {
  const key = (s || "").toLowerCase();
  if (key === "signed" || key === "completed") return "success";
  if (key === "out for signature" || key === "partially signed") return "warn";
  if (key === "draft") return "neutral";
  return "info";
}

export default function SignPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agreements");
        // Focus on agreements that are in the signature workflow
        setItems(
          [...data].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
        );
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const inFlight = items.filter((a) => ["Out for Signature", "Partially Signed"].includes(a.status));
  const signed = items.filter((a) => a.status === "Signed");
  const drafts = items.filter((a) => a.status === "Draft");

  return (
    <div data-testid="sign-page">
      <PageHeader
        eyebrow="La Signature"
        title="Sign"
        description="Send documents for signature, watch their status, and keep the audit trail with the executed copy."
        action={
          <Link to="/app/agreements">
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="sign-open-agreements-btn">
              <FileSignature size={14} className="mr-1.5" /> New signature request <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        }
      />

      {/* Status summary strip */}
      <div className="grid sm:grid-cols-4 gap-px bg-[#E8E3F0] border border-[#E8E3F0] mb-8">
        {[
          { label: "Drafts", value: drafts.length, tone: "neutral", icon: PenLine },
          { label: "Out for signature", value: inFlight.length, tone: "warn", icon: Share2 },
          { label: "Signed", value: signed.length, tone: "success", icon: ShieldCheck },
          { label: "Total", value: items.length, tone: "info", icon: FileSignature },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white p-5">
              <div className="flex items-center gap-2 mb-2 label-eyebrow"><Icon size={12} /> {s.label}</div>
              <div className="font-serif text-3xl">{s.value}</div>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="label-eyebrow">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          title="Nothing to sign yet"
          description="Open Agreements to draft or upload a document and send it for signature."
          action={<Link to="/app/agreements"><Button className="rounded-none bg-[#2A0F5A] text-white">Open Agreements</Button></Link>}
        />
      ) : (
        <div className="border border-[#E8E3F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E3F0]">
                <th className="text-left px-4 py-3 label-eyebrow">Document</th>
                <th className="text-left px-4 py-3 label-eyebrow">Status</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Updated</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8]" data-testid={`sign-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.title}</div>
                    {a.parties?.length > 0 && (
                      <div className="text-xs text-[#4A5568] mt-0.5">{a.parties.map((p) => p.name || p).slice(0, 3).join(" · ")}</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge tone={statusTone(a.status)}>{a.status || "Draft"}</StatusBadge></td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-[#4A5568]">{new Date(a.updated_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to="/app/agreements" className="text-xs underline text-[#2A0F5A] hover:text-[#7B56C9]">Manage</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
